import {
  ChevronsCollapseVertical,
  ChevronsExpandVertical,
} from "@gravity-ui/icons";
import { Button, Flex, Icon, Progress, Text, Tooltip } from "@gravity-ui/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { T } from "src/i18n";
import { JobsWithChildren } from "src/pages/ExportPage/store/fetchers";
import { truncateStart } from "src/util";

import { JobsTableActions } from "./components";
import { humanizeFailedReason } from "./humanizeFailedReason";
import { progressTheme, status } from "./JobsTable.constants";

const formatDateToDateAndTime = (date: Date) => {
  return { date: date.toLocaleDateString(), time: date.toLocaleTimeString() };
};

export const jobsTableColumns: ColumnDef<JobsWithChildren>[] = [
  {
    accessorKey: "id",
    header: ({ table }) => (
      <>
        <Button onClick={table.getToggleAllRowsExpandedHandler()} view="flat">
          <Icon
            data={
              table.getIsAllRowsExpanded()
                ? ChevronsCollapseVertical
                : ChevronsExpandVertical
            }
          />
        </Button>{" "}
        <Text variant="subheader-1">
          <T keyName="export.table.column.id" defaultValue="ID" />
        </Text>
      </>
    ),
    cell: ({ row, getValue }) => (
      <Text
        variant={"subheader-1"}
        style={{ paddingLeft: `${row.depth * 2}rem` }}
      >
        {row.getCanExpand() && (
          <Button onClick={row.getToggleExpandedHandler()} view="flat">
            <Icon
              data={
                row.getIsExpanded()
                  ? ChevronsCollapseVertical
                  : ChevronsExpandVertical
              }
            />
          </Button>
        )}{" "}
        {truncateStart(getValue<string>())}
      </Text>
    ),
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "name",
    cell: (info) => info.getValue(),
    header: () => (
      <Text variant="subheader-1">
        <T keyName="export.table.column.name" defaultValue="Имя" />
      </Text>
    ),
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "progress",
    cell: (info) => {
      const progress = info.getValue<number>();
      const isAborted = info.row.original.statuses?.isAborted;
      const jobStatus = isAborted
        ? "stopped"
        : (info.row.original.status as keyof typeof status);
      const isFullProgress = [
        "failed",
        "waiting",
        "delayed",
        "stopped",
      ].includes(jobStatus);

      const statuses = info.row.original?.statuses;
      const failedReason = humanizeFailedReason(
        info.row.original?.failedReason,
      );

      const hasWarnings = statuses?.warnings?.messages?.length > 0;
      const hasErrors =
        statuses?.errors?.messages?.length > 0 || !!failedReason;

      const theme = hasErrors
        ? "danger"
        : hasWarnings
          ? "warning"
          : progressTheme[jobStatus];

      const progressText =
        jobStatus === "active" ? `${progress.toFixed(1)}%` : status[jobStatus];
      // g-progress рисует подпись дважды (__text + __text-inner для inverted
      // contrast), и AT читает её как «Успешно. Успешно». Прячем native-разметку
      // от screen-reader, а accessible-name отдаём через role=status на обёртке.
      const accessibleLabel = failedReason
        ? `${status[jobStatus]}. ${failedReason}`
        : progressText;

      const bar = (
        <span role="status" aria-label={accessibleLabel}>
          <span aria-hidden="true">
            <Progress
              text={progressText}
              value={isFullProgress ? 100 : progress}
              theme={theme}
            />
          </span>
        </span>
      );

      return failedReason ? (
        <Tooltip content={failedReason} openDelay={200}>
          {bar}
        </Tooltip>
      ) : (
        bar
      );
    },
    header: () => (
      <Text variant="subheader-1">
        <T keyName="export.table.column.progress" defaultValue="Прогресс" />
      </Text>
    ),
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "createdAt",
    cell: (info) => {
      const value = info.getValue<Date>();

      if (value === undefined) return <Text>-</Text>;

      const { date, time } = formatDateToDateAndTime(new Date(value));

      return (
        <Flex direction={"column"}>
          <Text>{date}</Text>
          <Text>{time}</Text>
        </Flex>
      );
    },
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="export.table.column.createDate"
          defaultValue="Дата создания"
        />
      </Text>
    ),
    footer: (props) => props.column.id,
  },
  {
    accessorKey: "finishedAt",
    cell: (info) => {
      const value = info.getValue<Date>();
      if (value === undefined) return <Text>-</Text>;
      if (!value) return <Text>Ещё выполняется</Text>;

      const { date, time } = formatDateToDateAndTime(new Date(value));

      return (
        <Flex direction={"column"}>
          <Text>{date}</Text>
          <Text>{time}</Text>
        </Flex>
      );
    },
    header: () => (
      <Text variant="subheader-1">
        <T
          keyName="export.table.column.completeDate"
          defaultValue="Дата выполнения"
        />
      </Text>
    ),
    footer: (props) => props.column.id,
  },
  {
    id: "actions",
    cell: (info) => <JobsTableActions info={info} />,
    header: () => (
      <Text variant="subheader-1">
        <T keyName="export.table.column.actions" defaultValue="Действия" />
      </Text>
    ),
    footer: (props) => props.column.id,
  },
];
